import siteVisitsAPI from '../../../api/nexceo/siteVisits';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

const getters = {
  allSiteVisits: $state => $state.records,

  siteVisitsByLead: $state => leadId =>
    $state.records.filter(v => v.lead_id === leadId),

  siteVisitsByProject: $state => projectId =>
    $state.records.filter(v => v.project_id === projectId),

  getSiteVisitById: $state => id => $state.records.find(v => v.id === id),

  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  // M26.14 Part 5/13 — Live UI Synchronization. Called when the
  // nexceo_site_visit.updated websocket event fires (see helper/actionCable.js).
  // Refetches via the existing show endpoint and patches the store through
  // the existing UPDATE_SITE_VISIT mutation (a no-op if not currently loaded
  // in this view) — no new payload contract.
  refreshSiteVisit: async ({ commit }, siteVisitId) => {
    const { data } = await siteVisitsAPI.show(siteVisitId);
    commit('UPDATE_SITE_VISIT', data);
  },

  fetchSiteVisits: async ({ commit }, params = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await siteVisitsAPI.get(params);
      commit('SET_SITE_VISITS', data);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  createSiteVisit: async ({ commit }, visitData) => {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await siteVisitsAPI.create({ site_visit: visitData });
      // Backend now returns { site_visit, lead }; handle both shapes for safety.
      const siteVisit = data.site_visit ?? data;
      commit('ADD_SITE_VISIT', siteVisit);
      // Sync updated lead into nexceoLeads store so Kanban cards move immediately.
      if (data.lead) {
        commit('nexceoLeads/UPDATE_LEAD', data.lead, { root: true });
      }
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  updateSiteVisit: async ({ commit }, { id, ...visitData }) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await siteVisitsAPI.update(id, {
        site_visit: visitData,
      });
      commit('UPDATE_SITE_VISIT', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  confirmSiteVisit: async ({ commit }, id) => {
    const { data } = await siteVisitsAPI.confirm(id);
    commit('UPDATE_SITE_VISIT', data);
    return data;
  },

  completeSiteVisit: async ({ commit }, id) => {
    const { data } = await siteVisitsAPI.complete(id);
    commit('UPDATE_SITE_VISIT', data);
    return data;
  },

  cancelSiteVisit: async ({ commit }, { id, reason }) => {
    const { data } = await siteVisitsAPI.cancel(id, reason);
    commit('UPDATE_SITE_VISIT', data);
    return data;
  },

  rescheduleSiteVisit: async (
    { commit },
    { id, scheduledAt, endsAt, notes, location, assignedAgentId }
  ) => {
    const { data } = await siteVisitsAPI.reschedule(id, {
      scheduledAt,
      endsAt,
      notes,
      location,
      assignedAgentId,
    });
    commit('UPDATE_SITE_VISIT', data);
    return data;
  },

  deleteSiteVisit: async ({ commit }, id) => {
    await siteVisitsAPI.delete(id);
    commit('REMOVE_SITE_VISIT', id);
  },
};

const mutations = {
  SET_SITE_VISITS: ($state, visits) => {
    $state.records = visits;
  },

  ADD_SITE_VISIT: ($state, visit) => {
    $state.records.unshift(visit);
  },

  UPDATE_SITE_VISIT: ($state, visit) => {
    const idx = $state.records.findIndex(v => v.id === visit.id);
    if (idx !== -1) {
      $state.records.splice(idx, 1, visit);
    } else {
      $state.records.unshift(visit);
    }
  },

  REMOVE_SITE_VISIT: ($state, id) => {
    $state.records = $state.records.filter(v => v.id !== id);
  },

  SET_UI_FLAG: ($state, flags) => {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
